import { useEffect, useState } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, useLocation,
} from 'react-router-dom';
import { LoginScreen, EnrollScreen } from '../features/auth/AuthScreens';
import { HelpScreen } from '../features/help/HelpScreen';
import { SelfCheckScreen } from '../features/help/SelfCheckScreen';
import { EngineCheckScreen } from '../features/help/EngineCheckScreen';
import { DailyCheckGate } from '../features/help/DailyCheckGate';
import { InstallLock } from '../features/install/InstallGate';
import { LocationLock } from '../features/location/LocationLock';
import { CoverageScreen } from '../features/coverage/CoverageScreen';
import { GuideScreen } from '../features/guide/GuideScreen';
import { RouteHome } from '../features/route/RouteHome';
import { StopFlow } from '../features/stop-detail/StopFlow';
import { PrivacyScreen } from '../features/privacy/PrivacyScreen';
import { PrivacyGate } from '../features/privacy/PrivacyGate';
import { Footer } from '../features/shared/Footer';
import { getToken, sweepLeftovers } from '../core/offline/db';
import { initSync } from '../core/offline/syncManager';
import { ErrorBoundary } from './ErrorBoundary';

/**
 * Every screen opens at the top.
 *
 * A single-page app does not reset the scroll when the screen changes, so a
 * driver who had scrolled down their route list and then tapped "Need help"
 * arrived at the BOTTOM of the help page — past every phone number, which is
 * the entire reason that screen exists. Reported after it happened twice.
 *
 * Behavior, not decoration: somebody opening Help is usually standing at a
 * door with a problem, and they should not have to scroll up to find the
 * office number.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // 'instant' rather than smooth: an animated scroll on arrival looks like
    // the screen is still moving when the driver is trying to read it.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    } catch {
      window.scrollTo(0, 0);        // older Safari has no options object
    }
  }, [pathname]);
  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<'checking' | 'in' | 'out'>('checking');
  useEffect(() => {
    // getToken() opens IndexedDB, which CAN reject -- Safari private mode,
    // blocked site data, a failed upgrade. Without this catch the state stays
    // 'checking' forever and the driver stares at a blank screen with no
    // error and no way out. Treat an unreadable store as signed out.
    void getToken()
      .then((t) => setState(t ? 'in' : 'out'))
      .catch((err) => {
        console.error('token_read_failed', err);
        setState('out');
      });
  }, []);
  // Brief, and only while IndexedDB is being read.
  if (state === 'checking') return <main className="screen"><p>Loading…</p></main>;
  return state === 'in' ? <>{children}</> : <Navigate to="/login" replace />;
}

export function App() {
  useEffect(() => {
    initSync();
    // Clear what older versions of this app left in storage. Best effort.
    void sweepLeftovers();
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/enroll" element={<EnrollScreen />} />
          <Route path="/route" element={
            <InstallLock><RequireAuth><PrivacyGate><DailyCheckGate><LocationLock><RouteHome /></LocationLock></DailyCheckGate></PrivacyGate></RequireAuth></InstallLock>} />

          <Route path="/help" element={<RequireAuth><HelpScreen /></RequireAuth>} />
          {/* "Check my phone" — self-test; the office can text drivers this link (4 Oct). */}
          <Route path="/check" element={<RequireAuth><SelfCheckScreen /></RequireAuth>} />
          <Route path="/coverage" element={<RequireAuth><CoverageScreen /></RequireAuth>} />
          <Route path="/guide" element={<RequireAuth><GuideScreen /></RequireAuth>} />
          {/*
            * ONE route for a stop. There were briefly two — the old screen and
            * this one, at the same path — and React Router silently took the
            * first, so the rebuilt flow never ran and a whole test was spent on
            * the screen it replaced.
            */}
          <Route path="/stop/:taskId" element={
            <InstallLock><RequireAuth><PrivacyGate><LocationLock><StopFlow /></LocationLock></PrivacyGate></RequireAuth></InstallLock>} />
          {/* Readable without signing in: someone deciding whether to accept a
              job should be able to read what the app records about them. */}
          <Route path="/privacy" element={<PrivacyScreen />} />
          {/* Nightly robot test of the browser engine (15 Oct). No sign-in, no data. */}
          <Route path="/engine-check" element={<EngineCheckScreen />} />
          <Route path="*" element={<Navigate to="/route" replace />} />
        </Routes>
        <Footer />
      </ErrorBoundary>
    </BrowserRouter>
  );
}
